import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  FaSearch,
  FaMapMarkerAlt,
  FaStar,
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
  FaTag,
} from 'react-icons/fa'
import QuoteModal from '../components/QuoteModal'
import tourService from '../services/tourService'
import discountService from '../services/discountService'
import Loader from '../components/Loader'
import settingsService from '../services/settingsService'
import HeroSlider from '../components/HeroSlider'
import HomeAdSenseBanner from '../components/HomeAdSenseBanner'
import { getTourImage } from '../utils/helpers'
import SEO from '../utils/SEO'
import { generateFAQSchema } from '../utils/structuredData'
import Button from '../components/ui/Button'

const HOME_FAQS = [
  {
    question: 'What is the best time to visit Himachal Pradesh?',
    answer:
      'The best time to visit Himachal Pradesh is March to June for pleasant weather and October to February for snowfall. Monsoon (July-September) is best avoided due to landslides.',
  },
  {
    question: 'How do I book a tour package with Himachal Yatra?',
    answer:
      'You can browse our tour packages on the website, select your preferred tour, choose dates and number of travelers, and book online. You can also contact us for custom trip planning.',
  },
  {
    question: 'What popular destinations does Himachal Yatra cover?',
    answer:
      'We cover all major destinations including Shimla, Manali, Dharamshala, McLeod Ganj, Spiti Valley, Kullu, Kasol, Bir Billing, Dalhousie, and Kinnaur.',
  },
  {
    question: 'Are adventure activities included in tour packages?',
    answer:
      'Many of our tour packages include adventure activities like trekking, paragliding, river rafting, camping, and skiing. Check individual tour details for included activities.',
  },
  {
    question: 'What is the cancellation policy?',
    answer:
      'Cancellation policies vary by tour package. Please refer to our Cancellation Policy page or the specific tour details for refund and modification terms.',
  },
]

const Home = () => {
  const [allTours, setAllTours] = useState([])
  const [featuredTours, setFeaturedTours] = useState([])
  const [offers, setOffers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchLoading, setSearchLoading] = useState(false)
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  })
  const [searchParams, setSearchParams] = useState({
    search: '',
    destination: '',
    duration: '',
  })
  const [adsenseConfig, setAdsenseConfig] = useState(null)
  const [showQuoteModal, setShowQuoteModal] = useState(false)

  useEffect(() => {
    loadData()
    loadAdsenseConfig()
  }, [])

  const loadAdsenseConfig = async () => {
    try {
      const response = await settingsService.getPublicSettings('general')
      if (response.success && response.settings) {
        const s = response.settings
        const toBoolean = (v, def = false) => {
          if (v === undefined || v === null || v === '') return def
          return String(v).toLowerCase() === 'true' || String(v) === '1'
        }
        const normalizeClient = (v) => {
          const raw = (v || '').toString().trim()
          if (!raw) return ''
          if (raw.startsWith('ca-pub-')) return raw
          const digits = raw.replace(/[^0-9]/g, '')
          return digits ? `ca-pub-${digits}` : raw
        }
        setAdsenseConfig({
          enabled: toBoolean(s.home_adsense_enabled),
          client: normalizeClient(s.home_adsense_client || ''),
          slot: (s.home_adsense_slot || '').toString().trim(),
          testMode: toBoolean(s.home_adsense_test_mode, false),
        })
      }
    } catch (_) {
      /* silently skip if settings unavailable */
    }
  }

  const loadData = async () => {
    try {
      const [toursData, featuredData, offersData] = await Promise.all([
        tourService.getAllTours({ limit: 20, page: 1 }),
        tourService.getFeaturedTours(),
        discountService.getActiveOffers(),
      ])
      setAllTours(toursData.tours)
      setPagination({
        page: toursData.page,
        totalPages: toursData.totalPages,
        total: toursData.total,
      })
      setFeaturedTours(featuredData.tours)
      setOffers(
        offersData.offers.filter(
          (offer, i, arr) => arr.findIndex((o) => o.title === offer.title) === i,
        ),
      )
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearchChange = (e) => {
    setSearchParams({
      ...searchParams,
      [e.target.name]: e.target.value,
    })
  }

  const handleSearch = async () => {
    setSearchLoading(true)
    try {
      const params = {
        limit: 20,
        page: 1,
      }

      if (searchParams.search) params.search = searchParams.search
      if (searchParams.destination) params.destination = searchParams.destination
      if (searchParams.duration) params.duration = searchParams.duration

      const data = await tourService.getAllTours(params)
      setAllTours(data.tours)
      setPagination({
        page: data.page,
        totalPages: data.totalPages,
        total: data.total,
      })
    } catch (error) {
      console.error('Error searching tours:', error)
    } finally {
      setSearchLoading(false)
    }
  }

  const handlePageChange = async (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return

    setSearchLoading(true)
    try {
      const params = {
        limit: 20,
        page: newPage,
      }

      if (searchParams.search) params.search = searchParams.search
      if (searchParams.destination) params.destination = searchParams.destination
      if (searchParams.duration) params.duration = searchParams.duration

      const data = await tourService.getAllTours(params)
      setAllTours(data.tours)
      setPagination({
        page: data.page,
        totalPages: data.totalPages,
        total: data.total,
      })

      // Scroll to tours section
      document.getElementById('tours-section')?.scrollIntoView({ behavior: 'smooth' })
    } catch (error) {
      console.error('Error loading page:', error)
    } finally {
      setSearchLoading(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearch()
    }
  }

  // Check if search is active
  const isSearchActive = searchParams.search || searchParams.destination || searchParams.duration

  // Featured Tours Section Component
  const FeaturedToursSection = () => (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
            Featured Tours
          </h2>
          <p className="text-gray-600 text-lg">
            Top-rated experiences handpicked by our local team
          </p>
        </div>

        {loading ? (
          <Loader />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredTours.map((tour) => (
              <Link key={tour.id} to={`/tours/${tour.id}`} className="card group">
                <div className="relative h-64 overflow-hidden shine-on-hover">
                  <img
                    src={getTourImage(tour)}
                    alt={tour.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 right-4 bg-white/80 backdrop-blur-md px-3 py-1 rounded-full text-sm font-semibold text-primary-600 border border-white/40">
                    ₹{tour.base_price.toLocaleString()}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                    <FaMapMarkerAlt />
                    <span>{tour.destination}</span>
                    <span>•</span>
                    <span>{tour.duration} Days</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition">
                    {tour.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">{tour.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1">
                      {tour.review_count > 0 ? (
                        <>
                          <FaStar className="text-yellow-400" />
                          <span className="font-semibold">{tour.avg_rating}</span>
                          <span className="text-gray-500 text-sm">
                            ({tour.review_count} reviews)
                          </span>
                        </>
                      ) : (
                        <span className="bg-primary-100 text-primary-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                          New
                        </span>
                      )}
                    </div>
                    <span className="text-primary-600 font-semibold group-hover:underline">
                      See Full Itinerary →
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="text-center mt-12">
          <Button to="/tours" icon={<FaArrowRight />} className="flex-row-reverse">
            Explore All Tours
          </Button>
        </div>
      </div>
    </section>
  )

  // All Tours Section Component
  const AllToursSection = () => (
    <section id="tours-section" className={`py-16 ${isSearchActive ? 'bg-gray-50' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
            All Tours & Trips
          </h2>
          <p className="text-gray-600 text-lg">
            Showing {allTours.length} of {pagination.total} tours
          </p>
        </div>

        {searchLoading ? (
          <Loader />
        ) : allTours.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {allTours.map((tour) => (
                <Link key={tour.id} to={`/tours/${tour.id}`} className="card group">
                  <div className="relative h-48 overflow-hidden shine-on-hover">
                    <img
                      src={getTourImage(tour)}
                      alt={tour.title}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-3 right-3 bg-white/80 backdrop-blur-md px-2 py-1 rounded-full text-xs font-semibold text-primary-600 border border-white/40">
                      ₹{tour.base_price.toLocaleString()}
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center space-x-2 text-xs text-gray-500 mb-2">
                      <FaMapMarkerAlt />
                      <span>{tour.destination}</span>
                      <span>•</span>
                      <span>{tour.duration} Days</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition line-clamp-2">
                      {tour.title}
                    </h3>
                    <div className="flex items-center space-x-1 text-sm">
                      {tour.review_count > 0 ? (
                        <>
                          <FaStar className="text-yellow-400" />
                          <span className="font-semibold">{tour.avg_rating || '5.0'}</span>
                          <span className="text-gray-500 text-xs">({tour.review_count})</span>
                        </>
                      ) : (
                        <span className="bg-primary-100 text-primary-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                          New
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mt-12">
                <Button
                  variant="pagination"
                  size="sm"
                  icon={<FaChevronLeft />}
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1 || searchLoading}
                >
                  Previous
                </Button>

                <div className="flex space-x-2">
                  {[...Array(pagination.totalPages)].map((_, idx) => {
                    const pageNum = idx + 1
                    // Show first page, last page, current page, and pages around current
                    if (
                      pageNum === 1 ||
                      pageNum === pagination.totalPages ||
                      (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                    ) {
                      return (
                        <Button
                          key={pageNum}
                          variant={pageNum === pagination.page ? 'pagination-active' : 'pagination'}
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                          disabled={searchLoading}
                        >
                          {pageNum}
                        </Button>
                      )
                    } else if (pageNum === pagination.page - 2 || pageNum === pagination.page + 2) {
                      return (
                        <span key={pageNum} className="px-2 py-2">
                          ...
                        </span>
                      )
                    }
                    return null
                  })}
                </div>

                <Button
                  variant="pagination"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages || searchLoading}
                >
                  Next
                  <FaChevronRight className="ml-2" />
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              No tours found. Try adjusting your search criteria.
            </p>
          </div>
        )}
      </div>
    </section>
  )

  return (
    <div className="min-h-screen">
      <SEO
        title="Best Tour Packages in Himachal Pradesh"
        description="Book curated tour packages, adventure activities, hotels, and travel services in Himachal Pradesh. Explore Shimla, Manali, Dharamshala, Spiti Valley and more with Himachal Yatra."
        canonical="/"
        keywords="Himachal Pradesh tour packages, Shimla tours, Manali trip, adventure activities Himachal, trekking packages, travel booking India, Himachal Yatra"
        structuredData={generateFAQSchema(HOME_FAQS)}
      />
      {/* Hero Section with Slider */}
      <HeroSlider />

      {/* Search Section */}
      <section className="py-8 relative z-50 -mt-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative z-50 bg-white/70 backdrop-blur-xl rounded-xl shadow-2xl border border-white/30 p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="label">Search Tours</label>
                <input
                  type="text"
                  name="search"
                  placeholder="Search by name..."
                  value={searchParams.search}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyPress}
                  className="input-field"
                />
              </div>
              <div>
                <label className="label">Destination</label>
                <div className="relative">
                  <FaMapMarkerAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
                  <input
                    type="text"
                    name="destination"
                    placeholder="Where to?"
                    value={searchParams.destination}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyPress}
                    className="input-field !pl-10"
                  />
                </div>
              </div>
              <div>
                <label className="label">Duration</label>
                <select
                  name="duration"
                  value={searchParams.duration}
                  onChange={handleSearchChange}
                  className="input-field"
                >
                  <option value="">Any</option>
                  <option value="3">1-3 Days</option>
                  <option value="5">4-7 Days</option>
                  <option value="10">7+ Days</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button
                  icon={<FaSearch />}
                  fullWidth
                  onClick={handleSearch}
                  disabled={searchLoading}
                  loading={searchLoading}
                >
                  {searchLoading ? 'Searching...' : 'Search Tours'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conditionally render sections based on search status */}
      {isSearchActive ? (
        <>
          {/* Show All Tours first when searching */}
          <AllToursSection />
          {/* Show Featured Tours second */}
          <FeaturedToursSection />
        </>
      ) : (
        <>
          {/* Show Featured Tours first by default */}
          <FeaturedToursSection />
          {/* Show All Tours second */}
          <AllToursSection />
        </>
      )}

      {/* AdSense Banner – after tours */}
      <HomeAdSenseBanner config={adsenseConfig} className="my-4" />

      {/* Why Choose Us */}
      <section className="py-16 bg-gradient-to-br from-primary-50 via-white to-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Why Choose Himachal Yatra?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-white/60 backdrop-blur-lg rounded-xl border border-white/40 shadow-md hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-primary-100/80 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🎯</span>
              </div>
              <h3 className="text-xl font-bold mb-2">₹0 Middleman Markup</h3>
              <p className="text-gray-600">
                We run every tour ourselves. No agency fees, no inflated OTA prices. If you find a
                lower price, we'll match it.
              </p>
            </div>
            <div className="text-center p-6 bg-white/60 backdrop-blur-lg rounded-xl border border-white/40 shadow-md hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-primary-100/80 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🌟</span>
              </div>
              <h3 className="text-xl font-bold mb-2">4.8★ from 10,000+ Reviews</h3>
              <p className="text-gray-600">
                Real stories from verified travelers on Google and our site. Every review is from a
                confirmed booking.
              </p>
            </div>
            <div className="text-center p-6 bg-white/60 backdrop-blur-lg rounded-xl border border-white/40 shadow-md hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-primary-100/80 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🎁</span>
              </div>
              <h3 className="text-xl font-bold mb-2">Change Plans? No Stress.</h3>
              <p className="text-gray-600">
                Free date changes up to 7 days before departure. Full refund up to 48 hours. Because
                mountains don't follow schedules.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Special Offers */}
      {offers.length > 0 && (
        <section className="py-16 bg-gradient-to-r from-secondary-50 to-primary-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
                Special Offers
              </h2>
              <p className="text-gray-600 text-lg">
                Limited-time deals you won't find anywhere else
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {offers.slice(0, 2).map((offer) => (
                <div
                  key={offer.id}
                  className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{offer.title}</h3>
                      <p className="text-gray-600">{offer.description}</p>
                    </div>
                    <span className="bg-green-500 text-white px-4 py-2 rounded-full font-bold text-lg">
                      {offer.discount_percentage}% OFF
                    </span>
                  </div>
                  <Link to="/tours" className="text-primary-600 font-semibold hover:underline">
                    See What's Available →
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* AdSense Banner – before CTA */}
      <HomeAdSenseBanner config={adsenseConfig} className="mb-0" />

      {/* CTA Section */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
            The Himalayas Are Calling
          </h2>
          <p className="text-xl mb-8">Let&apos;s plan your dream Himalayan getaway</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              variant="secondary"
              to="/tour-planner"
              icon={<FaArrowRight />}
              className="bg-white flex-row-reverse"
            >
              Build Your Itinerary — Free
            </Button>
            <button onClick={() => setShowQuoteModal(true)} className="btn-hero-secondary">
              <FaTag /> Request Free Quote
            </button>
          </div>
        </div>
      </section>

      {/* Floating Quote FAB */}
      <button
        onClick={() => setShowQuoteModal(true)}
        aria-label="Request Free Quote"
        className="btn-hero !fixed bottom-6 left-6 z-40 !py-3 !px-4"
      >
        <FaTag size={14} />
        <span className="text-sm">Free Quote</span>
      </button>

      <QuoteModal isOpen={showQuoteModal} onClose={() => setShowQuoteModal(false)} />
    </div>
  )
}

export default Home
